import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, Download, Loader2, RefreshCw, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/primitives/Button";
import { api, type AspectRatio } from "@/lib/api";
import { directSegmentVideoUrl, type DirectSegment } from "@/lib/directApi";

// One clip of the batch: status, authenticated preview (blob), download, retry (only when failed).
export function SegmentCard({
  segment,
  aspect,
  canRetry,
  onRetry,
  busy,
  single,
}: {
  single?: boolean;
  segment: DirectSegment;
  aspect: AspectRatio;
  canRetry: boolean;
  onRetry: (index: number) => Promise<void>;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const ready = segment.status === "READY" && segment.has_output;

  useEffect(() => {
    if (!ready) {
      setSrc(null);
      return;
    }
    let url: string | null = null;
    let cancelled = false;
    api
      .get(directSegmentVideoUrl(segment.id), { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [segment.id, ready]);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(directSegmentVideoUrl(segment.id, true), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clip-${segment.index}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("direct.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const status =
    segment.status === "READY" ? (
      <span className="inline-flex items-center gap-1.5 text-[#3DDC84]"><CheckCircle2 className="h-3.5 w-3.5" /> {t("direct.clipReady")}</span>
    ) : segment.status === "GENERATING" ? (
      <span className="inline-flex items-center gap-1.5 text-[#7FD4FF]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("direct.clipGenerating")}</span>
    ) : segment.status === "FAILED" ? (
      <span className="inline-flex items-center gap-1.5 text-[#FF8A8A]"><XCircle className="h-3.5 w-3.5" /> {t("direct.clipFailed")}</span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {t("direct.clipPending")}</span>
    );

  return (
    <article data-testid={`segment-card-${segment.index}`} className="glass-panel flex flex-col gap-4 rounded-2xl p-4 sm:flex-row sm:items-start">
      <div
        className={cn(
          "relative shrink-0 overflow-hidden rounded-lg border border-[#2E7BFF]/35 bg-black",
          aspect === "9:16" ? "mx-auto aspect-[9/16] w-28 sm:mx-0 sm:w-32" : "aspect-video w-full sm:w-56",
        )}
      >
        {ready ? (
          src ? (
            <video data-testid={`segment-video-${segment.index}`} src={src} controls playsInline preload="metadata" className="h-full w-full object-contain" />
          ) : (
            <div className="grid h-full w-full place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#2E7BFF]" /></div>
          )
        ) : (
          <div className="grid h-full w-full place-items-center font-display text-3xl font-bold text-[#2E7BFF]/40">{String(segment.index).padStart(2, "0")}</div>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-base font-bold uppercase tracking-wide text-foreground">{single ? t("direct.video") : t("direct.clipN", { n: segment.index })}</h3>
          <span className="text-xs text-muted-foreground">{segment.duration_target_seconds ? `~${segment.duration_target_seconds} s` : ""}</span>
        </div>
        {segment.title && <p className="truncate text-xs text-muted-foreground">{segment.title}</p>}
        <p data-testid={`segment-status-${segment.index}`} className="text-xs">{status}</p>
        {segment.status === "FAILED" && segment.error && <p className="text-xs text-[#FF8A8A]/80">{segment.error}</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          {ready && (
            <Button type="button" variant="secondary" size="sm" data-testid={`segment-download-${segment.index}`} disabled={downloading} onClick={download}>
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} {t("common.downloadMp4")}
            </Button>
          )}
          {segment.status === "FAILED" && canRetry && (
            <Button type="button" size="sm" data-testid={`segment-retry-${segment.index}`} disabled={busy} onClick={() => onRetry(segment.index)}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {t("direct.retryClip")}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
